// Operator-facing renderer for a CONFIGURED duration held in whole seconds —
// the auction's staggered-close interval and its anti-snipe extension
// (`auctions.staggered_close_seconds` / `auctions.lot_extension_seconds`).
//
// Lives in @auctogon/time per ADR 024: time formatting belongs in this package,
// which exists specifically so formatters don't get re-implemented per app.
// This is the single renderer behind BL-1207 AC 3.
//
// Deliberately NOT built on `breakDuration` (./time-remaining), even though the
// arithmetic looks similar. That function is for COUNTDOWNS: it carries into
// hours and days (`minutes: … % 60`), so a 90-minute stagger would render as
// "1 hr 30 min". Operators reason about these two settings in minutes and
// seconds — a stagger control reading "90 min" is more useful than "1 hr 30
// min" — so this function keeps counting in minutes all the way to the column's
// 86400 ceiling. Different question, different function.

/**
 * Render a configured duration in whole seconds as operator-facing copy.
 *
 * Minutes never carry into hours (see the module note): 5400 → `"90 min"`, not
 * `"1 hr 30 min"`.
 *
 * @param seconds - Whole seconds. `0` is a real, meaningful value on both
 *   columns this serves (stagger 0 = every lot closes together; extension 0 =
 *   anti-snipe off) and renders as `"0 sec"`, never blank or a dash. Negative
 *   input floors at zero — the columns are CHECK-constrained to `>= 0`, so a
 *   negative can only come from a caller bug, and `"-1 min -30 sec"` helps
 *   nobody. Fractional input is truncated toward zero.
 * @returns `"0 sec"` | `"30 sec"` | `"3 min"` | `"1 min 30 sec"`.
 *
 * @sideEffect none — pure.
 */
export function formatDuration(seconds: number): string {
    const total = Number.isFinite(seconds) ? Math.max(0, Math.trunc(seconds)) : 0;

    const minutes = Math.floor(total / 60);
    const remainder = total % 60;

    if (minutes === 0) return `${remainder} sec`;
    if (remainder === 0) return `${minutes} min`;
    return `${minutes} min ${remainder} sec`;
}
